import React, { useEffect, useState } from "react";
import { CURRENCY_STRING } from "../../constants";
import { LoaderIcon } from "react-hot-toast";
import { useGetIntegrateAmounts } from "services/auths";
import usePricing from "../../hooks/usePricing";
import {useParams} from "react-router-dom";

const IntegrationType = [
    { title: "OTP Redirect", type: "redirect-ng" },
    { title: "OTP Direct", type: "otp-direct-ng" },
    { title: "2-Way Messaging", type: "2-way-sms-ng" },
    { title: "KYC Verification", type: "kyc-nin" },
    { title: "DeviceAuth", type: "device-auth" },
];

type PricingItem = {
    title: string;
    type: string;
    value: string;
};

const Pricing: React.FC = () => {
    const { userId: merchantUserId} = useParams()
    const { amount: amountsFromHook, loading } = useGetIntegrateAmounts(merchantUserId);

    const { updateProviderCost, loading: updating, error: pricingError, setError: setPricingError } = usePricing();

    const [data, setData] = useState<PricingItem[]>([]);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [editValue, setEditValue] = useState<string>("");
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const mapped = IntegrationType.map((item) => {
            const found = amountsFromHook?.find(
                (a: any) => a?.providerName === item.type
            );
            const amountStr =
                found && found.amount !== undefined && found.amount !== null
                    ? String(found.amount)
                    : "";
            return {
                title: item.title,
                type: item.type,
                value: amountStr ? CURRENCY_STRING + amountStr : "",
            };
        });
        setData(mapped);
    }, [amountsFromHook]);

    const startEdit = (idx: number) => {
        setError(null);
        setPricingError(null);
        setEditingIndex(idx);
        const current = data[idx]?.value?.replace(CURRENCY_STRING, "") || "";
        setEditValue(current);
    };

    const cancelEdit = () => {
        setError(null);
        setEditingIndex(null);
        setEditValue("");
    };

    const saveEdit = async (idx: number) => {
        setError(null);
        const item = data[idx];
        if (!item) return;
        const formattedAmount = editValue.trim();
        if (!formattedAmount || Number.isNaN(Number(formattedAmount)) || Number(formattedAmount) < 0) {
            setError("Enter a valid non-negative amount.");
            return;
        }

        try {
            await updateProviderCost(item.type, formattedAmount, merchantUserId);

            // update UI locally on success
            setData((prev) =>
                prev.map((d, i) =>
                    i === idx ? { ...d, value: CURRENCY_STRING + formattedAmount } : d
                )
            );
            setEditingIndex(null);
            setEditValue("");
        } catch (e: any) {
            setError(
                e?.message
                    ? `Update failed: ${e.message}`
                    : String(e?.toString() || "Update failed")
            );
        }
    };

    return (
        <div>
            {loading ? (
                <div className="flex w-full justify-center mt-6">
                    <LoaderIcon />
                </div>
            ) : (
                <div>
                    {data?.map((item, idx) => (
                        <div
                            key={idx}
                            className="flex items-center justify-between max-w-xl mt-6"
                        >
                            <div className="flex items-center gap-3">
                                <b>{item.title}</b>
                            </div>

                            <div className="flex items-center gap-3">
                                {editingIndex === idx ? (
                                    <>
                                        <input
                                            className="border rounded px-2 py-1 w-32"
                                            value={editValue}
                                            onChange={(e) => setEditValue(e.target.value)}
                                            inputMode="numeric"
                                            aria-label={`edit-${item.title}`}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => saveEdit(idx)}
                                            disabled={updating}
                                            className="px-3 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-500 disabled:opacity-50"
                                        >
                                            {updating ? "Saving..." : "Save"}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={cancelEdit}
                                            disabled={updating}
                                            className="px-3 py-1 rounded bg-gray-200 text-gray-800 hover:bg-gray-300 disabled:opacity-50"
                                        >
                                            Cancel
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <span className="min-w-[64px] text-right">{item.value || "N/A"}</span>
                                        <button
                                            type="button"
                                            onClick={() => startEdit(idx)}
                                            disabled={updating}
                                            className="px-3 py-1 rounded bg-gray-100 text-gray-800 hover:bg-gray-200 disabled:opacity-50"
                                        >
                                            Edit
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    ))}

                    {(error || pricingError) && (
                        <div className="text-red-600 mt-3">
                            {error || pricingError}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default Pricing;
